import { motion } from "framer-motion";
import { RefreshCw, MoreHorizontal, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Widget — the single primitive every Dashboard 2.0 panel uses.
 * Sizes:
 *   sm  → 1 col
 *   md  → 2 col (default)
 *   lg  → 3 col
 *   full → span all columns
 */
export const Widget = ({
  title,
  icon: Icon,
  size = "md",
  actions,
  onRefresh,
  refreshing,
  refreshLabel = "Refresh",
  viewAllTo,
  viewAllLabel = "View all",
  children,
  className = "",
  testId,
  padding = "p-5",
  headerBorder = false,
}) => {
  const span = {
    sm: "lg:col-span-1",
    md: "lg:col-span-2",
    lg: "lg:col-span-3",
    full: "lg:col-span-6",
  }[size] || "lg:col-span-2";

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className={`bg-card border border-border rounded-xl ${padding} ${span} ${className}`}
      data-testid={testId}
    >
      {(title || Icon || actions || onRefresh || viewAllTo) && (
        <div
          className={`flex items-center justify-between mb-4 ${headerBorder ? "pb-3 border-b border-border" : ""}`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {Icon && <Icon size={16} strokeWidth={1.75} className="text-primary flex-shrink-0" />}
            {title && (
              <h3 className="font-display font-bold text-base text-foreground truncate">{title}</h3>
            )}
          </div>
          <div className="flex items-center gap-1">
            {actions}
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                title={refreshLabel}
                aria-label={refreshLabel}
                className="p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground disabled:opacity-50"
                data-testid={testId ? `${testId}-refresh` : undefined}
              >
                <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} strokeWidth={1.75} />
              </button>
            )}
            {viewAllTo && (
              <Link
                to={viewAllTo}
                className="text-xs text-primary hover:underline font-medium flex items-center gap-0.5 ml-1"
                data-testid={testId ? `${testId}-view-all` : undefined}
              >
                {viewAllLabel} <ArrowUpRight size={12} strokeWidth={2} />
              </Link>
            )}
          </div>
        </div>
      )}
      {children}
    </motion.section>
  );
};

export const WidgetSkeleton = ({ height = "h-32" }) => (
  <div className={`skeleton rounded-lg w-full ${height}`} />
);

export const WidgetEmpty = ({ title, description, action, testId }) => (
  <div className="text-center py-6" data-testid={testId}>
    <div className="text-sm font-medium text-foreground mb-1">{title}</div>
    {description && <div className="text-xs text-muted-foreground mb-3">{description}</div>}
    {action}
  </div>
);

export const WidgetError = ({ message = "Something went wrong", onRetry }) => (
  <div className="text-center py-4">
    <div className="text-xs text-destructive mb-2">{message}</div>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="text-xs text-primary hover:underline font-medium"
      >
        Retry
      </button>
    )}
  </div>
);
